/**
 * PLT-7087 — keeps the web chat's product set cards current.
 *
 * Each card draws the tool result it was rendered from, and every edit renders a new card, so a
 * thread accumulates cards for the same company. Left alone, every one of them keeps its own product
 * count and a working "Looks good, compute results" button after the set has changed or been
 * approved, and again when the chat is reopened (cards are replayed from the stored results).
 *
 * The host knows what one card cannot: the rest of the thread. `productSetCardStanding` works out
 * whether a card has been replaced or approved, and only the company's current card re-reads the set
 * from the server, when it mounts and after each save from the card's editor, so edits and approvals
 * made outside the thread show on it too. The re-read is `get_dd_product_set`'s own result
 * (`/api/chat/product-set-cards/{companyId}`), whose `publishState` carries an approval made
 * elsewhere. The standing reaches the card as `cardState` on the payload. Claude and ChatGPT do not
 * run this hook, so there a card shows the result it was given.
 *
 * PLT-7456 — an approved card lists the products its approval recorded (`approvedProductSet` on the
 * publish result), not the result it was rendered from: a save from the card's editor writes no tool
 * result, so that one can predate the set approved, and an edit made since is not what was approved.
 * Until the publish follows, as while the site review its approval opened is still open, the set is
 * the draft that publish will approve, so the card reads it once. A publish result from before
 * publishes recorded the set leaves the card on the result it was rendered from.
 */
import { useThread } from '@assistant-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { subscribeProductSetSaved } from './product-set-saved-events';
import {
  approvedProductsOf,
  approvingPublishResult,
  companyIdOf,
  productSetCardStanding,
} from './product-set-card-standing';

/** A re-read worth showing: a set with products, not a no-data or error answer. */
function isReadyProductSet(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false;
  const { status, dataAvailable } = body as { status?: unknown; dataAvailable?: unknown };
  return status === 'ready' && dataAvailable !== false;
}

/** The payload to draw the product set card from: `toolResult`, current and placed in its thread. */
export function useProductSetCardPayload({
  toolCallId,
  toolResult,
}: {
  toolCallId: string;
  toolResult: unknown;
}): unknown {
  const companyId = companyIdOf(toolResult);
  const standing = useThread(state =>
    companyId === undefined ? 'current' : productSetCardStanding(state.messages, toolCallId, companyId),
  );
  const publishResult = useThread(state =>
    companyId === undefined ? undefined : approvingPublishResult(state.messages, toolCallId, companyId),
  );
  const [reread, setReread] = useState<unknown>(undefined);
  const isCurrent = standing === 'current';
  const readsSet = isCurrent || (standing === 'approved' && publishResult === undefined);
  const approvedProducts = standing === 'approved' ? approvedProductsOf(publishResult) : undefined;

  useEffect(() => {
    if (companyId === undefined || !readsSet) return;
    let cancelled = false;
    // Two quick saves start two re-reads, and the earlier one can answer last.
    let latestRequest = 0;
    const refresh = () => {
      const request = ++latestRequest;
      // Through the platform's fetcher, which resolves the API host: a bare `/api/...` path is not the
      // API on a UI preview host.
      fetchOrThrow<unknown>(`/chat/product-set-cards/${encodeURIComponent(companyId)}`)
        .then(body => {
          if (!cancelled && request === latestRequest && isReadyProductSet(body)) setReread(body);
        })
        .catch((err: unknown) => {
          // Best-effort: the card keeps the result it was rendered from.
          // eslint-disable-next-line no-console
          console.warn('[useProductSetCardPayload] re-read failed', err);
        });
    };
    refresh();
    // An approved card offers no editor, so only the current one has saves to follow.
    const unsubscribe = isCurrent
      ? subscribeProductSetSaved(savedCompanyId => {
          if (savedCompanyId === companyId) refresh();
        })
      : undefined;
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [readsSet, isCurrent, companyId]);

  return useMemo(() => {
    if (companyId === undefined) return toolResult;
    // A re-read only happens while the card is current or its approval is still to be published, so it
    // is never older than the stored result.
    const base = (reread ?? toolResult) as Record<string, unknown>;
    if (standing === 'current') return base;
    return { ...base, ...(approvedProducts ? { products: approvedProducts } : {}), cardState: standing };
  }, [companyId, reread, standing, approvedProducts, toolResult]);
}
